import React, { useState } from 'react';
import { useHistory } from 'react-router-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faSyncAlt, faCheckCircle, faTimesCircle, faClock, faHandPaper,
  faSearch, faTimes, faBuilding, faLink, faRedo,
} from '@fortawesome/free-solid-svg-icons';
import abacusService from '../../services/abacusService';
import { extractApiError } from '../../utils/errorMessages';
import HMSBadge from '../../components/ui/HMSBadge';
import HMSButton from '../../components/ui/HMSButton';
import HMSEmptyState from '../../components/ui/HMSEmptyState';
import '../../components/ui/HMSTable.css';

// Every IMT money document accounting has collected, and where it stands in its
// company's Abacus books. A document that can't go yet says exactly why, and the
// person who can fix it does it from here: match the customer or supplier once,
// or send it again once the reason is fixed (Chris's walk-through, 2026-10-21).

const MODEL_LINKS = {
  OutgoingInvoice: (id) => `/invoicing/outgoing/${id}`,
  IncomingInvoice: (id) => `/invoicing/incoming/${id}`,
  CreditNote: (id) => `/invoicing/credit-notes/${id}`,
  DebitNote: (id) => `/invoicing/debit-notes/${id}`,
};

const MODEL_LABELS = {
  OutgoingInvoice: 'Sales invoice',
  IncomingInvoice: 'Supplier invoice',
  CreditNote: 'Credit note',
  DebitNote: 'Debit note',
};

const STATE_BADGE = { SYNCED: 'approved', ERROR: 'rejected', WAITING: 'pending', PENDING: 'draft' };

const FILTERS = [
  { key: 'all', label: 'All documents', icon: faSyncAlt, total: (s) => s?.records?.length || 0 },
  { key: 'WAITING', label: 'Waiting for someone', icon: faHandPaper, total: (s) => s?.total_waiting || 0 },
  { key: 'ERROR', label: 'Refused by Abacus', icon: faTimesCircle, total: (s) => s?.total_errors || 0 },
  { key: 'PENDING', label: 'Being sent', icon: faClock, total: (s) => s?.total_queued || 0 },
  { key: 'SYNCED', label: 'In Abacus', icon: faCheckCircle, total: (s) => s?.total_in_abacus || 0 },
];

const formatDate = (isoStr) => {
  if (!isoStr) return '-';
  const d = new Date(isoStr);
  return d.toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' })
    + ' ' + d.toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit' });
};

const MatchDialog = ({ row, onClose, onDone }) => {
  const [number, setNumber] = useState('');
  const [error, setError] = useState('');
  const [saving, setSaving] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    const typed = number.trim();
    if (!/^\d+$/.test(typed)) {
      setError(`Type the Abacus ${row.partner_word} number, digits only.`);
      return;
    }
    setSaving(true);
    try {
      const result = await abacusService.matchPartner(row.partner_id, {
        books: row.books, side: row.side, abacus_id: typed,
      });
      onDone(result);
    } catch (err) {
      setError(extractApiError(err, `The ${row.partner_word} could not be matched.`));
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="md-modal-overlay" onClick={onClose}>
      <form className="md-modal" style={{ maxWidth: 480 }} onClick={(e) => e.stopPropagation()} onSubmit={submit}>
        <div className="md-modal-header">
          <div className="md-modal-header-text">
            <FontAwesomeIcon icon={faLink} className="md-modal-header-icon" />
            <h2>Match the {row.partner_word}</h2>
          </div>
          <button type="button" className="md-modal-close" onClick={onClose} aria-label="Close">
            <FontAwesomeIcon icon={faTimes} />
          </button>
        </div>
        <div className="md-modal-body">
          <p style={{ marginTop: 0 }}>
            Which Abacus {row.partner_word} is <strong>{row.partner_name}</strong> in {row.company}'s books
            ({row.books})? You only do this once: every later document for {row.partner_name} goes by itself.
          </p>
          <label htmlFor="abacus-partner-number" style={{ display: 'block', fontWeight: 600, marginBottom: 6 }}>
            Abacus {row.partner_word} number
          </label>
          <input
            id="abacus-partner-number"
            className="master-data-search-input"
            inputMode="numeric"
            value={number}
            onChange={(e) => { setNumber(e.target.value); setError(''); }}
            placeholder="10234"
            autoFocus
          />
          {error && <p className="abacus-reason-cell" role="alert" style={{ marginBottom: 0 }}>{error}</p>}
        </div>
        <div className="md-modal-footer">
          <button type="button" className="md-btn-cancel" onClick={onClose}>Cancel</button>
          <button type="submit" className="md-btn-submit" disabled={saving}>
            {saving ? 'Matching...' : 'Match and send'}
          </button>
        </div>
      </form>
    </div>
  );
};

const AbacusSyncStatusTab = ({ status, loading, refresh, showSuccess, showError }) => {
  const history = useHistory();
  const [activeFilter, setActiveFilter] = useState('WAITING');
  const [searchTerm, setSearchTerm] = useState('');
  const [matching, setMatching] = useState(null);
  const [sending, setSending] = useState(null);

  const records = (status?.records || []).filter((r) => {
    if (activeFilter !== 'all' && r.state !== activeFilter) return false;
    if (!searchTerm) return true;
    const term = searchTerm.toLowerCase();
    return [r.number, r.partner_name, r.company, r.reason, r.abacus_id]
      .some((v) => String(v || '').toLowerCase().includes(term));
  });

  const sendAgain = async (row) => {
    setSending(row.id);
    try {
      const result = await abacusService.sendAgain(row.model, row.document_id);
      if (showSuccess) showSuccess(result.message);
      refresh();
    } catch (err) {
      if (showError) showError(extractApiError(err, `${row.number} could not be sent again.`));
    } finally {
      setSending(null);
    }
  };

  const companies = status?.companies || [];

  return (
    <>
      <div className="search-filter-controls abacus-companies-row">
        <FontAwesomeIcon icon={faBuilding} className="abacus-companies-icon" />
        {companies.length === 0 ? (
          <span className="abacus-companies-note">
            No IMT company sends to Abacus yet. Add its Abacus books number under Corporate Structure.
          </span>
        ) : (
          companies.map((c) => (
            <span key={c.id} className="abacus-direction-badge">{c.name} · books {c.books}</span>
          ))
        )}
      </div>

      <div className="search-filter-controls abacus-filter-row">
        {FILTERS.map((f) => (
          <button
            type="button"
            key={f.key}
            className={`abacus-stat-card ${activeFilter === f.key ? 'abacus-stat-card--active abacus-stat-card--active-green' : ''}`}
            onClick={() => setActiveFilter(f.key)}
          >
            <FontAwesomeIcon icon={f.icon} className="abacus-stat-icon" />
            <div>
              <div className="abacus-stat-value">{f.total(status)}</div>
              <div className="abacus-stat-label">{f.label}</div>
            </div>
          </button>
        ))}
      </div>

      <div className="search-filter-controls">
        <div className="master-data-search-box">
          <FontAwesomeIcon icon={faSearch} className="search-icon" />
          <input
            type="text"
            className="master-data-search-input"
            placeholder="Search by number, partner, company or reason"
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
          />
          {searchTerm && (
            <button type="button" className="clear-search" onClick={() => setSearchTerm('')} aria-label="Clear search">
              <FontAwesomeIcon icon={faTimes} />
            </button>
          )}
        </div>
        <button type="button" className="reload-button" onClick={refresh} title="Reload" disabled={loading}>
          <FontAwesomeIcon icon={faSyncAlt} />
        </button>
      </div>

      <div className="invoice-list-container">
        {records.length === 0 ? (
          <HMSEmptyState
            variant="card"
            icon={<FontAwesomeIcon icon={faCheckCircle} />}
            title={searchTerm ? 'No results' : 'Nothing here'}
            hint={searchTerm
              ? 'No document matches your search.'
              : 'Documents appear here once accounting marks them collected on the Accounting Queue.'}
          />
        ) : (
          <div className="hms-table-wrap">
            <table className="hms-table">
              <thead>
                <tr>
                  <th scope="col">Document</th>
                  <th scope="col">Partner</th>
                  <th scope="col">Company</th>
                  <th scope="col" style={{ textAlign: 'center' }}>Abacus</th>
                  <th scope="col">What it needs, or Abacus's answer</th>
                  <th scope="col">Abacus number</th>
                  <th scope="col">Last change</th>
                  <th scope="col" className="hms-table-actions" aria-label="Actions" />
                </tr>
              </thead>
              <tbody>
                {records.map((row) => (
                  <tr key={row.id}>
                    <td className="invoice-number-cell">
                      <button type="button" className="abacus-link-button" onClick={() => history.push(MODEL_LINKS[row.model](row.document_id))}>
                        {row.number}
                      </button>
                      <div className="abacus-doc-kind">{MODEL_LABELS[row.model]}</div>
                    </td>
                    <td>{row.partner_name}</td>
                    <td>{row.company || '-'}{row.books ? ` (${row.books})` : ''}</td>
                    <td style={{ textAlign: 'center' }}>
                      <HMSBadge status={STATE_BADGE[row.state] || 'draft'}>{row.state_words}</HMSBadge>
                    </td>
                    <td className={row.state === 'ERROR' || row.state === 'WAITING' ? 'abacus-reason-cell' : ''}>
                      {row.reason || (row.state === 'SYNCED' ? 'Booked in Abacus.' : '-')}
                    </td>
                    <td className="abacus-mono-cell">{row.abacus_id || '-'}</td>
                    <td className="abacus-date-cell">{formatDate(row.last_change)}</td>
                    <td className="hms-table-actions">
                      {row.needs_match && (
                        <HMSButton variant="primary" size="compact" onClick={() => setMatching(row)}>
                          <FontAwesomeIcon icon={faLink} /> Match the {row.partner_word}
                        </HMSButton>
                      )}
                      {row.can_send_again && (
                        <HMSButton variant="secondary" size="compact" onClick={() => sendAgain(row)} disabled={sending === row.id}>
                          <FontAwesomeIcon icon={faRedo} /> {sending === row.id ? 'Sending...' : 'Send again'}
                        </HMSButton>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {matching && (
        <MatchDialog
          row={matching}
          onClose={() => setMatching(null)}
          onDone={(result) => {
            setMatching(null);
            if (showSuccess) {
              const n = result.documents_sent_again;
              showSuccess(`Matched. ${n} waiting document${n === 1 ? '' : 's'} sent to Abacus.`);
            }
            refresh();
          }}
        />
      )}
    </>
  );
};

export default AbacusSyncStatusTab;
